import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authFetch } from "../services/authAPI";
import moment from "moment";
import "moment/locale/fr";
import Select from "../components/Select";
import Field from "../components/Field";
import Modal from "../components/Modal";
import { PRIORITIES, STATUS_OPTIONS, STATUS_CLASSES, ROLE_LABELS } from "../constants/tickets";
import AuthContext from "../context/AuthContext";

export default function VehiclePage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { role } = useContext(AuthContext);
    const [vehicle, setVehicle] = useState(null);
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tickets, setTickets] = useState([]);

    async function fetchTickets() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/vehicles/${id}/tickets`);
            if (!response.ok) {
                setError("Une erreur est survenue");
                return;
            }
            const data = await response.json();
            setTickets(data);
        } catch (err) {
            setError("Erreur lors du chargement du véhicule.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        async function fetchVehicle() {
            try {
                const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/vehicles/${id}`);
                if (!response.ok) {
                    setError("Véhicule introuvable.");
                    return;
                }
                const data = await response.json();
                setVehicle(data);
            } catch (err) {
                setError("Erreur lors du chargement du véhicule.");
            } finally {
                setLoading(false);
            }
        }

        async function fetchBrands() {
            try {
                const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/vehicle_brands`);
                const data = await response.json();
                setBrands(data);
            } catch {
                // silently handled
            }
        }

        fetchVehicle();
        fetchTickets();
        fetchBrands();
    }, [id]);

    const [priority, setPriority] = useState("LOW");
    const [mileage, setMileage] = useState("");
    const [description, setDescription] =  useState("");

    const [showModal, setShowModal] = useState(false);
    const [violations, setViolations] = useState([]);

    async function handleCreateTicket() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/tickets`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    priority: priority,
                    vehicle: '/api/vehicles/' + vehicle.id,
                    mileage: mileage !== "" ? parseInt(mileage, 10) : null,
                    description: description
                }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                setViolations(errorData.violations ?? []);
                return;
            }
            setShowModal(false);
            setViolations([]);
            fetchTickets();
        } catch {
            // silently handled
        }
    }

    const BRAND_LABELS = Object.fromEntries(brands.map((b) => [b.value, b.label]));

    if (loading) {
        return (
            <section>
                <div className="card mb-4">
                    <div className="card-body py-5 px-5 text-center">
                        <p className="text-muted">Chargement...</p>
                    </div>
                </div>
            </section>
        );
    }

    if (error || !vehicle) {
        return (
            <section>
                <div className="card mb-4">
                    <div className="card-body py-5 px-5 text-center">
                        <p className="text-danger">{error ?? "Véhicule introuvable."}</p>
                        <button className="btn btn-link" onClick={() => navigate("/vehicles")}>
                            Retour aux véhicules
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <>
        <section>
            <Modal
                title={"Créer un ticket"}
                body={
                    <>
                        {violations.length > 0 && (
                            <div className="alert alert-danger d-flex align-items-center" role="alert" data-mdb-color="danger" data-mdb-alert-init>
                                <i className="fas fa-times-circle me-3"></i>
                                <ul className="mb-0 list-unstyled">
                                    {violations.map((v, i) => (
                                        <li key={i}>{v.message}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <Select
                            name="priority"
                            label="Priorité"
                            value={priority}
                            onChange={(e) => setPriority(e.target.value)}
                            options={PRIORITIES}
                        />
                        <Field
                            name="mileage"
                            label="Kilométrage"
                            value={mileage}
                            onChange={(e) => setMileage(e.target.value)}
                            type="number"
                        />
                        <Field
                            name="description"
                            label="Description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            type="text"
                        />
                    </>
                }
                button={{
                    close:"Annuler",
                    valid:"Créer"
                }}
                showModal={showModal}
                setShowModal={setShowModal}
                onSubmit={handleCreateTicket}
            />
            <div className="card mb-4">
                <div className="card-header py-3">
                    <div className="row py-3 align-items-center">
                        <div className="col-sm-6 text-center text-sm-start">
                            <h5 className="mb-4 mb-sm-0 mt-2">
                                {vehicle.licensePlateNumber}
                                {vehicle.name && (
                                    <span className="text-muted fw-normal ms-3">{vehicle.name}</span>
                                )}
                            </h5>
                            <p>{vehicle.isOperational ? "Opérationnel" : "Non opérationnel"}</p>
                        </div>
                        <div className="col-sm-6 text-center text-sm-end">
                            <button
                                type="button"
                                className="btn btn-primary me-2"
                                onClick={() => { setShowModal(true); setViolations([]); }}
                            >
                                Créer
                                <i className="fas fa-plus ms-2"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <section>
            {tickets.length > 0 ? (
                <div className="d-flex flex-column gap-3">
                    {tickets.map((ticket) => (
                        <div className="card" key={ticket.id}>
                            <div className="card-header">
                                <h5 className="card-title">
                                    {ticket.ticketNumber}
                                    <span className={"ms-2 badge bg-" + (STATUS_CLASSES[ticket.status] ?? "secondary")}>
                                        {STATUS_OPTIONS[ticket.status] ?? ticket.status}
                                    </span>
                                </h5>
                                {role === "ROLE_FLEET_MANAGER" && (
                                    <small className="text-muted mb-1" style={{ fontSize: "0.85rem" }}>
                                        Crée le {moment(ticket.createdAt).locale("fr").format("Do MMMM YYYY [à] HH:mm")} par un {ROLE_LABELS[ticket.createdBy.role]?.toLowerCase()}
                                    </small>
                                )}
                            </div>
                            <div className="card-body">
                                {ticket.mileage && (
                                    <small className="text-muted">{ticket.mileage.toLocaleString("fr-FR")} km</small>
                                )}
                                <p className="card-text">{ticket.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-muted text-center">Aucun ticket associé à ce véhicule.</p>
            )}
        </section>
    </>
    );
}
